/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolCondition/components/ColumnHeader';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';

export const getProtocolValueAmountColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
  name: string,
): SmartTableColDef => ({
  colId: `protocol_value:${name}:amount`,
  sortable: true,
  headerValueGetter: () => {
    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolValue(
        experimentOrProtocol,
        name,
      );
    if (protocolValue && protocolValue.is_output) {
      return 'Initial Amount';
    }
    return 'Amount Used';
  },
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    const constant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    return {
      toolPanelColumnName: `Amount - ${name}`,
      icon: ICONS.protocol_value.info.amount_used,
      secondaryHeader: name,
      isConstant: !!constant,
      experimentOrProtocol,
      protocolValue:
        GridSmartTableProtocolConditionService.getProtocolValue(
          experimentOrProtocol,
          name,
        ),
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolConditionService.getProtocolValueEditable(
      params,
      experimentOrProtocol,
      name,
    ),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return null;
    }

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    if (protocolValue && protocolValue.amount) {
      return `${protocolValue.amount} ${protocolValue.unit || ''}`;
    }

    return '';
  },
  cellRenderer: (params: CellRendererParams<ProtocolCondition>) => {
    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    if (!variableTemplate && !protocolValueConstant) {
      return null;
    }

    const fallback = (
      <DataGridPlaceholder
        params={params}
        editable={GridSmartTableProtocolConditionService.getProtocolValueEditable(
          params,
          experimentOrProtocol,
          name,
        )}
        placeholder="No amount"
      />
    );

    if (protocolValue && protocolValue.amount) {
      return protocolValue.amountUnitText || fallback;
    }

    return fallback;
  },
  valueSetter: (params: any): boolean => {
    if (!params.node.data) {
      return false;
    }

    const cellState =
      GridSmartTableProtocolConditionService.getCellState(params);
    if (cellState === 'loading') {
      return false;
    }

    const variableTemplate =
      GridSmartTableProtocolConditionService.getProtocolValueVariableTemplate(
        experimentOrProtocol,
        name,
      );
    const newValue = params.newValue;
    const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
    const match = newValue.replace(',', '.').match(regex);
    if (!match) {
      return false;
    }
    const amount = match[1];
    const unit = match[2].trim() || variableTemplate?.unit;

    const protocolValueConstant =
      GridSmartTableProtocolConditionService.getProtocolValueConstant(
        experimentOrProtocol,
        name,
      );

    const protocolValue =
      GridSmartTableProtocolConditionService.getProtocolConditionProtocolValue(
        params.node.data,
        experimentOrProtocol,
        name,
      ) || protocolValueConstant;

    if (protocolValue) {
      if (protocolValue.guid[0] === '-') {
        return false;
      }

      return GridSmartTableProtocolConditionService.updateEntity(
        params,
        protocolValue.entityName,
        protocolValue.idAttr,
        {
          amount,
          unit,
        },
      );
    }

    if (!variableTemplate) {
      return false;
    }

    const body: { [key: string]: any } = {
      [`${experimentOrProtocol.entityName}_id`]:
        experimentOrProtocol.guid,
      variable_template_guid: variableTemplate.guid,
      ...(params.node.data?.id && {
        protocol_condition_id: params.node.data.id,
      }),
      amount: amount,
      unit: unit
        ? unit
        : variableTemplate
          ? variableTemplate.unit
          : undefined,
    };

    return GridSmartTableProtocolConditionService.createEntity(
      params,
      ProtocolValue.entityName,
      body,
      'protocol_condition',
      params.node.data.guid,
    );
  },
  cellStyle: (params) =>
    GridSmartTableProtocolConditionService.getCellStyle(
      params,
      experimentOrProtocol,
      ProtocolValue.entityName,
      name,
    ),
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
  valueClearer: () => {
    return '0';
  },
});
